import { useState, useCallback, useRef } from "react";
import { Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CopyButtonProps {
  value: string;
  ariaLabel?: string;
  size?: "default" | "sm";
}

export function CopyButton({ value, ariaLabel = "Copier", size = "default" }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!value) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const el = document.createElement("textarea");
        el.value = value;
        el.style.cssText = "position:fixed;opacity:0;pointer-events:none;";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopied(true);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
    }
  }, [value]);

  if (!value) return null;

  const iconCls = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      onClick={handleCopy}
      aria-label={ariaLabel}
      title={ariaLabel}
      data-testid={`btn-copy-${ariaLabel.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
    >
      {copied
        ? <Check className={`${iconCls} text-green-500`} />
        : <Copy className={iconCls} />}
    </Button>
  );
}
